/**
 * _statsBlocks.jsx : sous-blocs visuels de SessionStats (privés au dossier
 * sessions/, convention D52) : carte de statistique, badge de niveau et
 * couleurs de palier. Sans état ni logique métier : les paliers viennent de
 * classifyPm, classifyEnv et classifyTimeAbove.
 */
import React from 'react';
import { getTheme, SHADOW } from '../constants/theme';
import { semanticColors } from '../constants/semanticColors';
import { classifyPm, PM_LEVEL_BADGE } from '../constants/classifyPm';
import { classifyTimeAbove } from '../constants/classifyTimeAbove';

// Mapping accentColor (solide) → couleur texte sémantique AAA
// Les solides restent pour borderLeft et tint bg, le texte utilise *Text
export function semanticTextForAccent(accentColor) {
  if (accentColor === semanticColors.greenSolid) return semanticColors.greenText;
  if (accentColor === semanticColors.amberSolid) return semanticColors.amberText;
  if (accentColor === semanticColors.redSolid) return semanticColors.redText;
  return accentColor;
}

// Accent solide (bordure de carte, badge en sombre) par palier de la moyenne
// (classifyPm, classifyEnv). Le texte en dérive via semanticTextForAccent.
const LEVEL_ACCENT = {
  bon: semanticColors.greenSolid,
  a_surveiller: semanticColors.amberSolid,
  eleve: semanticColors.redSolid,
  non_mesure: semanticColors.neutralText,
};

export function levelAccent(level) {
  return LEVEL_ACCENT[level] || semanticColors.neutralText;
}

// Couleur du palier moyen d'un PM (bordure de carte) : classifyPm, source unique.
export function pmAccent(param, avg) {
  return levelAccent(classifyPm(avg, param).level);
}

// % temps > seuil d'avertissement : tri-couleur D3 (classifyTimeAbove).
const TIME_ABOVE_ACCENT = {
  low: semanticColors.greenSolid,
  mid: semanticColors.amberSolid,
  high: semanticColors.redSolid,
};

export function pctAccent(pct) {
  return TIME_ABOVE_ACCENT[classifyTimeAbove(pct)] || null;
}

// Badge du niveau de la moyenne : texte toujours présent (jamais la couleur
// seule), libellé accessible « Niveau de la moyenne : … ». Clair : tokens
// PM_LEVEL_BADGE (texte AAA sur fond pastel) ; sombre : solide sur fond
// transparent, comme les bordures de carte.
export function LevelBadge({ level, text, ariaLabel, darkMode }) {
  const tokens = PM_LEVEL_BADGE[level] || PM_LEVEL_BADGE.non_mesure;
  const color = darkMode ? levelAccent(level) : semanticColors[tokens.textToken];
  return (
    <span data-testid="level-badge" data-level={level} aria-label={ariaLabel} title={ariaLabel}
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
        fontSize: '11px', fontWeight: '700', lineHeight: 1.5, color,
        background: darkMode ? 'transparent' : semanticColors[tokens.bgToken],
        border: `1px solid ${color}`,
      }}>
      {text}
    </span>
  );
}

function StatLine({ line, darkMode }) {
  // line.accent (D3 % temps tri-couleur) : solide en sombre, token AAA en clair.
  const color = line.accent
    ? (darkMode ? line.accent : semanticTextForAccent(line.accent))
    : getTheme(darkMode).textSecondary;
  return (
    <div style={{ fontSize: '11px', color, fontWeight: line.accent ? '600' : '400', marginTop: '3px' }}>
      {line.text}
    </div>
  );
}

// Carte : libellé, puis valeur + unité + badge, puis lignes, puis « Détails »
// optionnel (élément details natif : fermé par défaut, clavier compris).
export function StatCard({ testId, label, value, unit, badge, lines, details, accentColor, darkMode }) {
  const theme = getTheme(darkMode);
  // Couleur texte sémantique (foncée AAA) pour libellé et bordure ; solide en sombre.
  const textAccent = darkMode ? accentColor : semanticTextForAccent(accentColor);

  return (
    <div data-testid={testId} style={{
      background: theme.surface, borderRadius: '12px', padding: '14px 16px', minWidth: 0,
      borderLeft: `5px solid ${textAccent}`, boxShadow: darkMode ? 'none' : SHADOW.card,
    }}>
      <div style={{ fontSize: '11px', fontWeight: '700', color: textAccent,
        textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px 8px', marginBottom: '4px' }}>
        <span style={{ fontSize: '28px', fontWeight: '700', color: theme.textPrimary, lineHeight: '1' }}>
          {value ?? '—'}
          {unit && <span style={{ fontSize: '13px', fontWeight: '400', color: theme.textSecondary, marginLeft: '4px' }}>{unit}</span>}
        </span>
        {badge}
      </div>
      {lines && lines.map((line, i) => <StatLine key={i} line={line} darkMode={darkMode} />)}
      {details && (
        <details data-testid={testId && `${testId}-details`} style={{ marginTop: '6px' }}>
          <summary style={{ fontSize: '11px', fontWeight: '600', color: theme.textSecondary, cursor: 'pointer' }}>
            {details.summary}
          </summary>
          {details.lines.map((line, i) => <StatLine key={i} line={line} darkMode={darkMode} />)}
        </details>
      )}
    </div>
  );
}
